import {
  AsyncEventDispatcher,
  AudioManager,
  AudioManagerPool,
  AudioResourceManager,
  EventDispatcher,
  PlaybackStatus,
  ValueDispatcher,
  type AudioClipConfig,
  type AudioTrackConfig,
  generateAudioId,
  type PlayerLike,
  type PlayerSettings,
  type PlayerState,
  type RemotionSceneDescription,
  type Logger,
} from '@cascade/core';
import type {PlayerRef} from '@remotion/player';
import {RemotionPlaybackManager} from './RemotionPlaybackManager';
import type {RemotionSceneWrapper} from './RemotionSceneWrapper';

/**
 * Self-contained adapter that bridges a Remotion PlayerRef to the
 * PlayerLike interface used by all Cascade UI components.
 *
 * Creates its own PlaybackManager, PlaybackStatus, and audio managers
 * internally -- no external MC objects needed.
 */
export class RemotionPlayerAdapter implements PlayerLike {
  // ── Event dispatchers ─────────────────────────────────────────────────
  private readonly _render = new AsyncEventDispatcher<void>();
  private readonly _state: ValueDispatcher<PlayerState>;
  private readonly _frame = new ValueDispatcher(0);
  private readonly _duration: ValueDispatcher<number>;
  private readonly _recalculated = new EventDispatcher<void>();
  public readonly onAudioClipsChanged = new ValueDispatcher<AudioClipConfig[]>(
    [],
  );
  public readonly onAudioTracksChanged = new ValueDispatcher<AudioTrackConfig[]>(
    [],
  );

  // ── Exposed subscribables ───────────────────────────────────────────
  public get onRender() {
    return this._render.subscribable;
  }
  public get onStateChanged() {
    return this._state.subscribable;
  }
  public get onFrameChanged() {
    return this._frame.subscribable;
  }
  public get onDurationChanged() {
    return this._duration.subscribable;
  }
  public get onRecalculated() {
    return this._recalculated.subscribable;
  }

  // ── Sub-objects required by PlayerLike ─────────────────────────────────
  // Typed as `any` to satisfy PlayerLike's `PlaybackManager` type.
  // At runtime this is a RemotionPlaybackManager with the same public API.
  public readonly playback: any;
  public readonly status: PlaybackStatus;
  public readonly audioPool: AudioManagerPool;
  public readonly audioResources: AudioResourceManager;
  public readonly logger: Logger;

  private ref: PlayerRef | null = null;
  private _fps: number;
  private active = false;
  private _loop = true;

  // Project-level audio clip managers (keyed by clip.id)
  private clipManagers = new Map<string, AudioManager>();
  private clipConfigs = new Map<string, AudioClipConfig>();
  private audioTracks: AudioTrackConfig[] = [];
  private audioContext: AudioContext | null = null;
  private hot: {send(event: string, data?: any): void} | null = null;
  private onPublishAudioClipsCb:
    | ((clips: AudioClipConfig[], tracks: AudioTrackConfig[]) => void)
    | null = null;

  constructor(opts: {
    fps: number;
    remotionScenes: RemotionSceneDescription[];
    logger: Logger;
    initialAudioClips?: AudioClipConfig[];
    initialAudioTracks?: AudioTrackConfig[];
    hot?: {send(event: string, data?: any): void};
    onPublishAudioClips?: (clips: AudioClipConfig[], tracks: AudioTrackConfig[]) => void;
  }) {
    this._fps = opts.fps;
    this.logger = opts.logger;
    this.hot = opts.hot ?? null;
    this.onPublishAudioClipsCb = opts.onPublishAudioClips ?? null;

    // Create our own playback manager from Remotion scene descriptions
    this.playback = new RemotionPlaybackManager(opts.fps);
    this.playback.setupFromDescriptions(opts.remotionScenes);

    // PlaybackStatus wraps the playback manager (reads .fps, .frame, .speed, .state)
    this.status = new PlaybackStatus(this.playback as any);

    // Audio: standalone managers for waveform display and playback
    this.audioResources = new AudioResourceManager(opts.logger);
    this.audioPool = new AudioManagerPool(opts.logger, this.audioResources);

    const totalDuration = this.playback.duration;
    this._duration = new ValueDispatcher(totalDuration);
    this._state = new ValueDispatcher<PlayerState>({
      paused: true,
      loop: true,
      muted: false,
      volume: 1,
      speed: 1,
    });

    // Load initial audio tracks and clips (with migration for legacy data)
    if (opts.initialAudioClips) {
      let tracks = opts.initialAudioTracks ?? [];
      let clips = opts.initialAudioClips;

      // Helper: detect invalid IDs (empty, undefined/null values, or the
      // literal strings 'undefined'/'null' produced by broken serialization)
      const isInvalidId = (v: string | undefined | null) =>
        !v || v === 'undefined' || v === 'null';

      // Migration: if clips lack id/trackId or no tracks provided, create defaults
      const needsMigration =
        tracks.length === 0 ||
        clips.some(c => isInvalidId(c.id) || isInvalidId(c.trackId));

      if (needsMigration) {
        // Ensure at least one default track exists
        if (tracks.length === 0) {
          tracks = [
            {
              id: generateAudioId(),
              name: 'Track 1',
              volume: 1,
              muted: false,
            },
          ];
        }
        const defaultTrackId = tracks[0].id;

        // Assign missing id/trackId to clips
        clips = clips.map(c => ({
          ...c,
          id: isInvalidId(c.id) ? generateAudioId() : c.id,
          trackId: isInvalidId(c.trackId) ? defaultTrackId : c.trackId,
        }));
      }

      this.audioTracks = tracks;
      this.onAudioTracksChanged.current = tracks;
      this.onAudioClipsChanged.current = clips;
      this.setupClipManagers(clips);

      // Persist migrated data so project.ts gets id/trackId fields
      if (needsMigration) {
        this.publishAudioClips(clips);
      }
    } else if (opts.initialAudioTracks) {
      this.audioTracks = opts.initialAudioTracks;
      this.onAudioTracksChanged.current = opts.initialAudioTracks;
    }
  }

  // ── Remotion PlayerRef lifecycle ─────────────────────────────────────
  public attachRef(ref: PlayerRef) {
    this.detachRef();
    this.ref = ref;
    ref.addEventListener('frameupdate', this.handleFrameUpdate);
    ref.addEventListener('play', this.handlePlay);
    ref.addEventListener('pause', this.handlePause);
    ref.addEventListener('ended', this.handleEnded);
    ref.addEventListener('seeked', this.handleSeeked);
  }

  public detachRef() {
    if (!this.ref) return;
    this.ref.removeEventListener('frameupdate', this.handleFrameUpdate);
    this.ref.removeEventListener('play', this.handlePlay);
    this.ref.removeEventListener('pause', this.handlePause);
    this.ref.removeEventListener('ended', this.handleEnded);
    this.ref.removeEventListener('seeked', this.handleSeeked);
    this.ref = null;
  }

  // ── Event handlers from Remotion ────────────────────────────────────
  private handleFrameUpdate = (e: {detail: {frame: number}}) => {
    const frame = e.detail.frame;
    this._frame.current = frame;
    this.playback.syncFrame(frame);
    this.syncClipAudio(frame);
    this._render.dispatch();
  };

  private handlePlay = () => {
    this._state.current = {...this._state.current, paused: false};
    this.syncClipPaused(false);
  };

  private handlePause = () => {
    this._state.current = {...this._state.current, paused: true};
    this.syncClipPaused(true);
  };

  private handleEnded = () => {
    this._state.current = {...this._state.current, paused: true};
    this.syncClipPaused(true);
  };

  private handleSeeked = (e: {detail: {frame: number}}) => {
    const frame = e.detail.frame;
    this._frame.current = frame;
    this.playback.syncFrame(frame);
    this.syncClipAudio(frame);
  };

  // ── Project-level audio clip management ──────────────────────────────
  private getAudioContext(): AudioContext {
    this.audioContext ??= new AudioContext();
    return this.audioContext;
  }

  private setupClipManagers(clips: AudioClipConfig[]) {
    // Dispose old managers
    for (const manager of this.clipManagers.values()) {
      manager.dispose();
    }
    this.clipManagers.clear();
    this.clipConfigs.clear();

    // Create a new AudioManager for each clip, keyed by clip.id
    for (const clip of clips) {
      const manager = new AudioManager(this.logger, this.getAudioContext());
      manager.setSource(clip.src);
      manager.setOffset(clip.offset);
      this.clipManagers.set(clip.id, manager);
      this.clipConfigs.set(clip.id, clip);
      // Pre-load audio resource for waveform display
      this.audioResources.get(clip.src);
    }
  }

  private getTrackForClip(clipId: string): AudioTrackConfig | undefined {
    const config = this.clipConfigs.get(clipId);
    if (!config) return undefined;
    return this.audioTracks.find(t => t.id === config.trackId);
  }

  private syncClipAudio(frame: number) {
    const time = frame / this._fps;
    const {paused, muted: globalMuted, volume: globalVolume} =
      this._state.current;
    for (const [clipId, manager] of this.clipManagers) {
      manager.setTime(time);
      const inRange = manager.isInRange(time);
      const track = this.getTrackForClip(clipId);
      const trackMuted = track?.muted ?? false;
      const trackVolume = track?.volume ?? 1;
      manager.setVolume(globalVolume * trackVolume);
      manager.setPaused(paused || globalMuted || trackMuted || !inRange);
    }
  }

  private syncClipPaused(paused: boolean) {
    const time = this._frame.current / this._fps;
    const globalMuted = this._state.current.muted;
    for (const [clipId, manager] of this.clipManagers) {
      const inRange = manager.isInRange(time);
      const track = this.getTrackForClip(clipId);
      const trackMuted = track?.muted ?? false;
      manager.setPaused(paused || globalMuted || trackMuted || !inRange);
    }
  }

  // ── Playback controls ───────────────────────────────────────────────
  public requestSeek(frame: number) {
    this.ref?.seekTo(this.clampRange(frame));
  }

  public requestPreviousFrame() {
    this.requestSeek(this._frame.current - 1);
  }

  public requestNextFrame() {
    this.requestSeek(this._frame.current + 1);
  }

  public requestReset() {
    this.requestSeek(0);
  }

  public requestRender() {
    this._render.dispatch();
  }

  public togglePlayback(value?: boolean) {
    if (!this.ref) return;
    const shouldPlay = value ?? this._state.current.paused;
    if (shouldPlay) {
      this.ref.play();
    } else {
      this.ref.pause();
    }
  }

  public toggleLoop(value?: boolean) {
    this._loop = value ?? !this._state.current.loop;
    this._state.current = {...this._state.current, loop: this._loop};
  }

  public toggleAudio(value?: boolean) {
    if (!this.ref) return;
    const shouldUnmute = value ?? this._state.current.muted;
    if (shouldUnmute) {
      this.ref.unmute();
    } else {
      this.ref.mute();
    }
    const globalMuted = !shouldUnmute;
    this._state.current = {...this._state.current, muted: globalMuted};
    // Propagate to clip audio managers, considering per-track mute state
    for (const [clipId, manager] of this.clipManagers) {
      const track = this.getTrackForClip(clipId);
      const trackMuted = track?.muted ?? false;
      manager.setMuted(globalMuted || trackMuted);
    }
  }

  public setAudioVolume(value: number) {
    const clamped = Math.max(0, Math.min(1, value));
    this.ref?.setVolume(clamped);
    this._state.current = {...this._state.current, volume: clamped};
    // Update clip managers with global * track volume
    for (const [clipId, manager] of this.clipManagers) {
      const track = this.getTrackForClip(clipId);
      const trackVolume = track?.volume ?? 1;
      manager.setVolume(clamped * trackVolume);
    }
  }

  public addAudioVolume(value: number) {
    this.setAudioVolume(this._state.current.volume + value);
  }

  public setSpeed(value: number) {
    this._state.current = {...this._state.current, speed: value};
  }

  // ── Range helpers ───────────────────────────────────────────────────
  public clampRange(frame: number): number {
    return Math.max(0, Math.min(this._duration.current, Math.round(frame)));
  }

  public isInRange(frame: number): boolean {
    return frame >= 0 && frame <= this._duration.current;
  }

  public isInUserRange(frame: number): boolean {
    return this.isInRange(frame);
  }

  // ── Audio clip management ───────────────────────────────────────────
  public addAudioClip(config: AudioClipConfig) {
    const clips = [...this.onAudioClipsChanged.current, config];
    this.onAudioClipsChanged.current = clips;
    this.setupClipManagers(clips);
    this.publishAudioClips(clips);
  }

  public removeAudioClip(clipId: string) {
    const clips = this.onAudioClipsChanged.current.filter(c => c.id !== clipId);
    this.onAudioClipsChanged.current = clips;
    this.setupClipManagers(clips);
    this.publishAudioClips(clips);
  }

  public setAudioClipOffset(clipId: string, offset: number) {
    const clips = this.onAudioClipsChanged.current.map(c =>
      c.id === clipId ? {...c, offset} : c,
    );
    this.onAudioClipsChanged.current = clips;
    this.setupClipManagers(clips);
    this.publishAudioClips(clips);
  }

  private publishAudioClips(clips: AudioClipConfig[]) {
    if (this.hot) {
      this.hot.send('cascade:set-audio-clips', {
        clips,
        tracks: this.audioTracks,
      });
    }
    this.onPublishAudioClipsCb?.(clips, this.audioTracks);
  }

  // ── Audio track management ──────────────────────────────────────────
  public addAudioTrack(track: AudioTrackConfig) {
    this.audioTracks = [...this.audioTracks, track];
    this.onAudioTracksChanged.current = this.audioTracks;
    this.publishAudioClips(this.onAudioClipsChanged.current);
  }

  public removeAudioTrack(trackId: string) {
    this.audioTracks = this.audioTracks.filter(t => t.id !== trackId);
    this.onAudioTracksChanged.current = this.audioTracks;
    // Also remove all clips that belonged to this track
    const clips = this.onAudioClipsChanged.current.filter(
      c => c.trackId !== trackId,
    );
    this.onAudioClipsChanged.current = clips;
    this.setupClipManagers(clips);
    this.publishAudioClips(clips);
  }

  public updateAudioTrack(
    trackId: string,
    updates: Partial<Pick<AudioTrackConfig, 'name' | 'volume' | 'muted'>>,
  ) {
    this.audioTracks = this.audioTracks.map(t =>
      t.id === trackId ? {...t, ...updates} : t,
    );
    this.onAudioTracksChanged.current = this.audioTracks;

    // Re-sync clip audio state so volume/mute changes take effect immediately
    const frame = this._frame.current;
    this.syncClipAudio(frame);

    this.publishAudioClips(this.onAudioClipsChanged.current);
  }

  // ── Scene management (no-ops for Remotion) ──────────────────────────
  public reorderScenes() {}
  public removeScene() {}
  public setVariables() {}

  // ── Lifecycle ───────────────────────────────────────────────────────
  public async configure(_settings: PlayerSettings) {
    // Remotion handles FPS/size through component props
  }

  public activate() {
    this.active = true;
  }

  public deactivate() {
    this.active = false;
    this.detachRef();
  }

  // ── Getters for Remotion component props ────────────────────────────
  /** The computed scene wrappers with frame offsets from the playback manager. */
  public get sceneWrappers(): RemotionSceneWrapper[] {
    return (this.playback as RemotionPlaybackManager).scenes;
  }

  public get fps() {
    return this._fps;
  }

  public get loop() {
    return this._state.current.loop;
  }

  public get playbackRate() {
    return this._state.current.speed;
  }

  public get durationInFrames() {
    return this._duration.current;
  }
}
